import type { RingDraft } from './flag-drafts';
import type { FlagManager } from './useFlag';
import { useState } from 'react';
import { MAX_FLAG_RINGS } from 'definitions';
import { toast } from '@/components/ui/sonner';
import { EMPTY_RING, parseRingDrafts, ringToDraft, sameRings } from './flag-drafts';

/**
 * The rings editor: each ring's side, rules and IDs as typed, saved together. Rings change who
 * sees what on a live flag too; the API republishes the website after they are saved.
 */
export const useRingsEditor = (manager: FlagManager, savedMessage: string) => {
  const savedRings = manager.rollout?.rings ?? [];
  const [drafts, setDrafts] = useState<RingDraft[] | null>(null);

  const rings = drafts ?? savedRings.map(ringToDraft);
  const parsed = parseRingDrafts(rings);
  const isDirty = drafts !== null && (!parsed.ok || !sameRings(parsed.value, savedRings));
  // Rings change who gets On, so a live flag's take experiments:start (useFlag's canEditAudience).
  const isLocked = manager.isArchived || !manager.canEditAudience;

  const edit = (next: RingDraft[]) => setDrafts(next);
  const updateRing = (index: number, patch: Partial<RingDraft>) =>
    edit(rings.map((ring, idx) => (idx === index ? { ...ring, ...patch } : ring)));

  const save = async () => {
    if (!isDirty || !parsed.ok) return;
    if (await manager.savePlan({ rings: parsed.value })) {
      setDrafts(null);
      toast.success(savedMessage);
    }
  };

  return {
    rings,
    isLocked,
    canAdd: !isLocked && rings.length < MAX_FLAG_RINGS,
    addRing: () => edit([...rings, EMPTY_RING]),
    removeRing: (index: number) => edit(rings.filter((_, idx) => idx !== index)),
    updateRing,
    isDirty,
    blockingReason: parsed.ok ? undefined : parsed.reason,
    isSaving: manager.pending === 'plan',
    save,
    discard: () => setDrafts(null),
  };
};

export type RingsEditorManager = ReturnType<typeof useRingsEditor>;
